import { WEAPON_POWERUPS, type SnapshotDTO } from "../protocol/index.js";
import {
  MSG_SNAPSHOT,
  MSG_SNAPSHOT_SLIM,
  bulletFlags,
  ds,
  encAngle,
  flagStateCode,
  pupCode,
  u16,
} from "./codes.js";

// --- snapshot --------------------------------------------------------------

// Per-weapon charge bytes replace the old single weaponCode+charges pair, so
// a tank can carry several combined weapon effects at once.
const TANK_BYTES = 18 + WEAPON_POWERUPS.length; // was 20 (2 weapon bytes); now one charge byte per weapon
const SLIM_TANK_BYTES = 11;

export function encodeSnapshot(s: SnapshotDTO): Uint8Array {
  const size =
    2 + // tag + tank count
    s.tanks.length * TANK_BYTES +
    1 +
    s.bullets.length * 8 +
    1 +
    s.powerups.length * 5 +
    1 +
    s.flags.length * 7 +
    1 +
    s.blasts.length * 4 +
    1 +
    s.beams.length * 8 +
    1 +
    s.events.length * 7 +
    2 +
    s.wallHp.length * 3; // u16 count, then u16 index + u8 hp per damaged wall
  const dv = new DataView(new ArrayBuffer(size));
  let o = 0;

  dv.setUint8(o++, MSG_SNAPSHOT);
  dv.setUint8(o++, s.tanks.length);
  for (const t of s.tanks) {
    dv.setUint8(o++, t.index);
    dv.setUint16(o, u16(t.x), true);
    o += 2;
    dv.setUint16(o, u16(t.y), true);
    o += 2;
    dv.setInt16(o, encAngle(t.bodyAngle), true);
    o += 2;
    dv.setInt16(o, encAngle(t.turretAngle), true);
    o += 2;
    let flags = 0;
    if (t.alive) flags |= 1;
    if (t.boosted) flags |= 2;
    if (t.shielded) flags |= 4;
    if (t.charging) flags |= 8;
    if (t.scoped) flags |= 16;
    dv.setUint8(o++, flags);
    dv.setUint8(o++, Math.min(255, t.hp));
    dv.setUint8(o++, Math.min(255, t.ammo));
    dv.setUint16(o, u16(t.score), true);
    o += 2;
    dv.setUint8(o++, ds(t.respawnIn));
    dv.setUint8(o++, ds(t.reloadIn));
    for (const w of WEAPON_POWERUPS) dv.setUint8(o++, Math.min(255, t.weaponCharges[w] ?? 0));
    dv.setUint8(o++, Math.min(255, t.livesLeft));
    dv.setUint8(o++, Math.min(255, t.captures));
  }

  // Owner is sent as the tank's wire index (resolved back to color client-side).
  // 255 = unknown owner.
  const ownerIndex = new Map(s.tanks.map((t) => [t.id, t.index]));
  const bulletCount = Math.min(255, s.bullets.length);
  dv.setUint8(o++, bulletCount);
  for (let bi = 0; bi < bulletCount; bi++) {
    const b = s.bullets[bi];
    dv.setUint16(o, b.id & 0xffff, true);
    o += 2;
    dv.setUint16(o, u16(b.x), true);
    o += 2;
    dv.setUint16(o, u16(b.y), true);
    o += 2;
    dv.setUint8(o++, bulletFlags(b));
    dv.setUint8(o++, ownerIndex.get(b.ownerId) ?? 255);
  }

  const powerupCount = Math.min(255, s.powerups.length);
  dv.setUint8(o++, powerupCount);
  for (let pi = 0; pi < powerupCount; pi++) {
    const p = s.powerups[pi];
    dv.setUint8(o++, pupCode(p.type));
    dv.setUint16(o, u16(p.x), true);
    o += 2;
    dv.setUint16(o, u16(p.y), true);
    o += 2;
  }

  dv.setUint8(o++, s.flags.length);
  for (const f of s.flags) {
    dv.setUint8(o++, f.team);
    dv.setUint8(o++, flagStateCode(f.state));
    dv.setUint16(o, u16(f.x), true);
    o += 2;
    dv.setUint16(o, u16(f.y), true);
    o += 2;
    dv.setUint8(o++, f.carrier & 0xff);
  }

  // Counts are single bytes, so clamp to 255 (a dense branching-beam volley can
  // exceed it); writing more than the count says would desync the reader.
  const blastCount = Math.min(255, s.blasts.length);
  dv.setUint8(o++, blastCount);
  for (let i = 0; i < blastCount; i++) {
    const bl = s.blasts[i];
    dv.setUint16(o, u16(bl.x), true);
    o += 2;
    dv.setUint16(o, u16(bl.y), true);
    o += 2;
  }

  const beamCount = Math.min(255, s.beams.length);
  dv.setUint8(o++, beamCount);
  for (let i = 0; i < beamCount; i++) {
    const bm = s.beams[i];
    dv.setUint16(o, u16(bm.x1), true);
    o += 2;
    dv.setUint16(o, u16(bm.y1), true);
    o += 2;
    dv.setUint16(o, u16(bm.x2), true);
    o += 2;
    dv.setUint16(o, u16(bm.y2), true);
    o += 2;
  }

  dv.setUint8(o++, s.events.length);
  for (const e of s.events) {
    dv.setUint8(o++, e.type);
    dv.setUint8(o++, e.killer);
    dv.setUint8(o++, e.victim);
    dv.setInt16(o, Math.max(-32767, Math.min(32767, e.points)), true);
    o += 2;
    dv.setUint8(o++, e.streak & 0xff);
    dv.setUint8(o++, Math.min(255, e.mult));
  }

  // Damaged walls (destructibleWalls only): index + current HP. The index is
  // u16 because per-cell destructible walls can exceed 255 on large maps.
  dv.setUint16(o, s.wallHp.length, true);
  o += 2;
  for (const w of s.wallHp) {
    dv.setUint16(o, w.index, true);
    o += 2;
    dv.setUint8(o++, Math.min(255, w.hp));
  }

  return new Uint8Array(dv.buffer);
}

/**
 * Smaller high-frequency snapshot. Tank stats that change slowly (score, ammo,
 * weapon charges, etc.) are refreshed by periodic full snapshots; this frame
 * keeps only pose + visual status for smooth interpolation between them.
 */
export function encodeSlimSnapshot(s: SnapshotDTO): Uint8Array {
  const size =
    2 +
    s.tanks.length * SLIM_TANK_BYTES +
    1 +
    s.bullets.length * 8 +
    1 +
    s.powerups.length * 5 +
    1 +
    s.flags.length * 7 +
    1 +
    s.blasts.length * 4 +
    1 +
    s.beams.length * 8 +
    1 +
    s.events.length * 7;
  const dv = new DataView(new ArrayBuffer(size));
  let o = 0;

  dv.setUint8(o++, MSG_SNAPSHOT_SLIM);
  dv.setUint8(o++, s.tanks.length);
  for (const t of s.tanks) {
    dv.setUint8(o++, t.index);
    dv.setUint16(o, u16(t.x), true);
    o += 2;
    dv.setUint16(o, u16(t.y), true);
    o += 2;
    dv.setInt16(o, encAngle(t.bodyAngle), true);
    o += 2;
    dv.setInt16(o, encAngle(t.turretAngle), true);
    o += 2;
    let flags = 0;
    if (t.alive) flags |= 1;
    if (t.boosted) flags |= 2;
    if (t.shielded) flags |= 4;
    if (t.charging) flags |= 8;
    if (t.scoped) flags |= 16;
    dv.setUint8(o++, flags);
    dv.setUint8(o++, ds(t.respawnIn));
  }

  o = encodeSnapshotTail(dv, o, s);
  return new Uint8Array(dv.buffer.slice(0, o));
}

function encodeSnapshotTail(dv: DataView, offset: number, s: SnapshotDTO): number {
  let o = offset;
  const ownerIndex = new Map(s.tanks.map((t) => [t.id, t.index]));
  const bulletCount = Math.min(255, s.bullets.length);
  dv.setUint8(o++, bulletCount);
  for (let bi = 0; bi < bulletCount; bi++) {
    const b = s.bullets[bi];
    dv.setUint16(o, b.id & 0xffff, true);
    o += 2;
    dv.setUint16(o, u16(b.x), true);
    o += 2;
    dv.setUint16(o, u16(b.y), true);
    o += 2;
    dv.setUint8(o++, bulletFlags(b));
    dv.setUint8(o++, ownerIndex.get(b.ownerId) ?? 255);
  }

  const powerupCount = Math.min(255, s.powerups.length);
  dv.setUint8(o++, powerupCount);
  for (let pi = 0; pi < powerupCount; pi++) {
    const p = s.powerups[pi];
    dv.setUint8(o++, pupCode(p.type));
    dv.setUint16(o, u16(p.x), true);
    o += 2;
    dv.setUint16(o, u16(p.y), true);
    o += 2;
  }

  dv.setUint8(o++, s.flags.length);
  for (const f of s.flags) {
    dv.setUint8(o++, f.team);
    dv.setUint8(o++, flagStateCode(f.state));
    dv.setUint16(o, u16(f.x), true);
    o += 2;
    dv.setUint16(o, u16(f.y), true);
    o += 2;
    dv.setUint8(o++, f.carrier & 0xff);
  }

  // Counts are single bytes, so clamp to 255 (a dense branching-beam volley can
  // exceed it); writing more than the count says would desync the reader.
  const blastCount = Math.min(255, s.blasts.length);
  dv.setUint8(o++, blastCount);
  for (let i = 0; i < blastCount; i++) {
    const bl = s.blasts[i];
    dv.setUint16(o, u16(bl.x), true);
    o += 2;
    dv.setUint16(o, u16(bl.y), true);
    o += 2;
  }

  const beamCount = Math.min(255, s.beams.length);
  dv.setUint8(o++, beamCount);
  for (let i = 0; i < beamCount; i++) {
    const bm = s.beams[i];
    dv.setUint16(o, u16(bm.x1), true);
    o += 2;
    dv.setUint16(o, u16(bm.y1), true);
    o += 2;
    dv.setUint16(o, u16(bm.x2), true);
    o += 2;
    dv.setUint16(o, u16(bm.y2), true);
    o += 2;
  }

  dv.setUint8(o++, s.events.length);
  for (const e of s.events) {
    dv.setUint8(o++, e.type);
    dv.setUint8(o++, e.killer);
    dv.setUint8(o++, e.victim);
    dv.setInt16(o, Math.max(-32767, Math.min(32767, e.points)), true);
    o += 2;
    dv.setUint8(o++, e.streak & 0xff);
    dv.setUint8(o++, Math.min(255, e.mult));
  }
  return o;
}
